import { useState } from "react"

import { StepHeader, WizardLayout } from "@/components/agents/setup/shell"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"

import { useStepSave } from "./nav"
import { OptionCard } from "./option-card"
import type { ResponseTone, ReviewResponderStepProps } from "./types"

const TONE_OPTIONS: {
  value: ResponseTone
  label: string
  description: string
  example: string
}[] = [
  {
    value: "professional",
    label: "Professional",
    description:
      "Polished and precise. Best for healthcare, finance, or legal.",
    example:
      "Thank you for taking the time to share your feedback. We appreciate your kind words and look forward to welcoming you again.",
  },
  {
    value: "warm",
    label: "Warm",
    description: "Friendly and genuine. Works for most local businesses.",
    example:
      "Thank you so much — this really made our day! We loved having you and can't wait to see you again soon.",
  },
  {
    value: "conversational",
    label: "Conversational",
    description:
      "Relaxed and natural. Great for hospitality or consumer brands.",
    example:
      "Aw, thanks! So glad you had a great time — means a lot to the whole team. See you next time!",
  },
]

function readTone(data: Record<string, unknown>): ResponseTone {
  const v = data.response_tone
  return TONE_OPTIONS.some((o) => o.value === v) ? (v as ResponseTone) : "warm"
}

function readString(data: Record<string, unknown>, key: string): string {
  const v = data[key]
  return typeof v === "string" ? v : ""
}

function readBool(
  data: Record<string, unknown>,
  key: string,
  fallback: boolean,
): boolean {
  const v = data[key]
  return typeof v === "boolean" ? v : fallback
}

export function ToneStep({
  agent,
  stepNumber,
  totalSteps,
  exampleSet,
}: ReviewResponderStepProps) {
  const { processing, save } = useStepSave(agent.uid)
  const [tone, setTone] = useState<ResponseTone>(() =>
    readTone(agent.setup_data),
  )
  const [signOff, setSignOff] = useState(() =>
    readString(agent.setup_data, "sign_off_name"),
  )
  const [matchLanguage, setMatchLanguage] = useState(() =>
    readBool(agent.setup_data, "match_language", true),
  )
  const [discloseAi, setDiscloseAi] = useState(() =>
    readBool(agent.setup_data, "disclose_ai", false),
  )

  const data = {
    response_tone: tone,
    sign_off_name: signOff,
    match_language: matchLanguage,
    disclose_ai: discloseAi,
  }

  const activeTone = TONE_OPTIONS.find((o) => o.value === tone)
  // IMP-305: industry-themed example when available, else the general one.
  const exampleText = exampleSet?.tone[tone] ?? activeTone?.example

  return (
    <WizardLayout
      footer={
        <>
          <Button
            variant="outline"
            onClick={() => save("step-connect", data)}
            disabled={processing}
          >
            Back
          </Button>
          <Button
            onClick={() => save("step-behavior", data)}
            disabled={processing}
          >
            Continue
          </Button>
        </>
      }
    >
      <StepHeader
        eyebrow={`Step ${stepNumber} of ${totalSteps}`}
        title="Tone & voice"
        description="Choose how the agent sounds when it responds to reviews."
      />

      <div className="space-y-3">
        <Label>Response style</Label>
        <div className="grid grid-cols-3 gap-2">
          {TONE_OPTIONS.map((opt) => (
            <OptionCard
              key={opt.value}
              selected={tone === opt.value}
              onClick={() => setTone(opt.value)}
              title={opt.label}
              description={opt.description}
            />
          ))}
        </div>

        {exampleText && (
          <div className="bg-muted/60 overflow-hidden rounded-lg border p-4">
            <p className="text-muted-foreground mb-2 text-xs font-medium tracking-wide uppercase">
              Example
            </p>
            <p className="text-sm leading-relaxed">
              &ldquo;{exampleText}&rdquo;
            </p>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="sign_off_name">Sign-off name</Label>
        <Input
          id="sign_off_name"
          value={signOff}
          onChange={(e) => setSignOff(e.target.value)}
          placeholder="e.g., The Team at Acme"
        />
        <p className="text-muted-foreground text-xs">
          Appended to responses. Leave blank to omit.
        </p>
      </div>

      <div className="space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-medium">Match review language</p>
            <p className="text-muted-foreground mt-1 text-xs">
              Reply in the same language the reviewer wrote in.
            </p>
          </div>
          <Switch checked={matchLanguage} onCheckedChange={setMatchLanguage} />
        </div>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-medium">Disclose AI assistance</p>
            <p className="text-muted-foreground mt-1 text-xs">
              Add a brief note that responses are AI-assisted.
            </p>
          </div>
          <Switch checked={discloseAi} onCheckedChange={setDiscloseAi} />
        </div>
      </div>
    </WizardLayout>
  )
}
